import "./verification.scss";

import React, { useEffect, useRef, useState } from "react";

import logo from "../../logo192.png";
import showToast from "../../utils/show.toast";
import { useNavigate } from "react-router-dom";

export default function OtpLogin() {
  const useEmail = localStorage.getItem("user_email");
  const sessionId = localStorage.getItem("loginSessionId");
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  useEffect(() => {
    if (token) {
      navigate("/dashboard");
    }
  }, [token, navigate]);

  const [otp, setOtp] = useState(Array(6).fill(""));
  const inputsRef = useRef<HTMLInputElement[]>([]);

  const handleChange = (value: string, index: number) => {
    const newOtp = [...otp];
    newOtp[index] = value.replace(/[^0-9]/g, "");
    setOtp(newOtp);

    if (value && index < otp.length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = e.clipboardData.getData("Text").trim();
    if (/^\d+$/.test(pasteData)) {
      const digits = pasteData.split("").slice(0, otp.length);
      const newOtp = [...otp];
      digits.forEach((d, i) => {
        newOtp[i] = d;
        if (inputsRef.current[i]) inputsRef.current[i].value = d;
      });
      setOtp(newOtp);
      // focus last filled input : In beta mood
      inputsRef.current[digits.length - 1]?.focus();
      e.preventDefault();
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const otpCode = otp.join("");
    const payload = { sessionId, otp: otpCode };

    try {
      const res = await fetch(
        "https://core.pluritongues.com/auth/login/verify-2fa",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const data = await res.json();

      if (data.status && data.result.accessToken) {
        showToast("Account verified successfully!", true);
        await localStorage.setItem("token", data.result.accessToken);
        navigate("/dashboard");
      } else {
        showToast(data.message || "Verification failed", false);
      }
    } catch (err) {
      showToast("Something went wrong, please try again.", false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="w-full max-w-md bg-white shadow-lg rounded-lg p-8">
        <div className="flex flex-col items-center mb-6">
          <div className="mb-4">
            <img src={logo} alt="Pluritongue_logo" className="h-20 w-20" />
          </div>
          <h2 className="text-2xl font-bold text-brand">
            Admin Account Verification
          </h2>
          <p className="text-gray-600 text-sm mt-2 text-center">
            We have sent a code to your email{" "}
            <span className="text-black font-semibold">{useEmail}</span>
          </p>
        </div>

        <form className="space-y-6 py-5" onSubmit={handleSubmit}>
          <div className="flex justify-center gap-3 mb-3">
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={(el) => (inputsRef.current[i] = el!)}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(e.target.value, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                onPaste={handlePaste}
                className="w-12 h-12 text-center text-lg border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
              />
            ))}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 px-4  bg-brand text-white font-semibold rounded-md shadow hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
          >
            {loading ? "Verifying..." : "Verify Account"}
          </button>

          <div className="text-center text-sm text-gray-600">
            <p>
              Didn’t receive code?{" "}
              <a
                href="/login"
                className="text-brand font-medium hover:underline"
              >
                Resend
              </a>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
